import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsService } from '../services/api';
import { JobModal } from '../components/JobModal';
import { formatMoney } from '../utils/pricing';
import { formatApptDateLong, formatApptTime, localDateKey } from '../utils/dateFormatters';
import { JOB_STATUSES, OPEN_STATUSES, jobStatusLabel, jobStatusClass } from '../utils/jobs';
import './Jobs.css';

const FILTERS = [{ value: 'open', label: 'Open' }, { value: 'all', label: 'All' }, ...JOB_STATUSES];
const money = (v) => (Number(v) < 0 ? `-${formatMoney(Math.abs(Number(v)))}` : formatMoney(v));

/**
 * Jobs — one row per piece of work: the client, its visits, the quote that
 * counts, what it has cost so far and the margin. Open a row for the job view.
 */
export default function Jobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [q, setQ] = useState('');
  const [modal, setModal] = useState(false);

  const load = useCallback(async () => {
    const r = await jobsService.getAll(q.trim() ? { q: q.trim() } : {});
    if (r.success) setJobs(r.jobs || []);
    setLoading(false);
  }, [q]);
  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0); // debounce typing
    return () => clearTimeout(t);
  }, [load, q]);

  const visible = jobs.filter((j) => (filter === 'all' ? true : filter === 'open' ? OPEN_STATUSES.includes(j.status) : j.status === filter));

  return (
    <div className="jobs-page">
      <div className="page-header">
        <h1 className="page-title">Jobs</h1>
        <button className="btn btn-primary" onClick={() => setModal(true)}>+ New Job</button>
      </div>

      <div className="jobs-toolbar">
        <div className="job-filters">
          {FILTERS.map((f) => (
            <button key={f.value} className={`job-filter ${filter === f.value ? 'active' : ''}`} onClick={() => setFilter(f.value)}>{f.label}</button>
          ))}
        </div>
        <input className="jobs-search" type="search" placeholder="Search job number, title or client…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search jobs" />
      </div>

      {loading ? (
        <div className="empty-state"><div className="loading-spinner"></div></div>
      ) : visible.length === 0 ? (
        <div className="empty-state"><p>{jobs.length === 0 ? 'No jobs yet. Every booking and every quote creates one, or start one here.' : 'Nothing here.'}</p></div>
      ) : (
        <div className="job-list">
          {visible.map((j) => (
            <button key={j.id} className="job-row" onClick={() => navigate(`/jobs/${j.id}`)}>
              <div className="job-row-main">
                <h3>{j.job_number} · {j.title || 'Untitled job'}</h3>
                <p className="text-muted">
                  {j.customer_name || 'No client'}{j.suburb ? ` · ${j.suburb}` : ''}
                  {j.visit_count ? ` · ${j.visit_count} visit${j.visit_count === 1 ? '' : 's'}` : ' · no visits'}
                  {j.first_visit ? ` · ${formatApptDateLong(j.first_visit)} ${formatApptTime(j.first_visit)} → ${localDateKey(j.first_visit) !== localDateKey(j.last_visit_end) ? `${formatApptDateLong(j.last_visit_end)} ` : ''}${formatApptTime(j.last_visit_end)}` : ' · not scheduled'}
                </p>
              </div>
              <div className="job-row-money">
                <span className="job-money">
                  <span className="job-money-label">Quoted</span>
                  <strong>{j.quoted_total === null ? '—' : formatMoney(j.quoted_total)}</strong>
                  {j.quote_number && <span className="job-money-sub">{j.quote_number} · {j.quote_status}</span>}
                </span>
                <span className="job-money">
                  <span className="job-money-label">Costs</span>
                  <strong>{j.cost_count ? formatMoney(j.cost_total) : '—'}</strong>
                  {j.cost_count > 0 && <span className="job-money-sub">{j.cost_count} line{j.cost_count === 1 ? '' : 's'}</span>}
                </span>
                <span className={`job-money ${j.profit === null || j.profit === undefined ? '' : j.profit < 0 ? 'neg' : 'pos'}`} title={j.overhead_working || ''}>
                  <span className="job-money-label">Profit</span>
                  <strong>{j.profit === null || j.profit === undefined ? '—' : money(j.profit)}</strong>
                  {j.overhead_share ? <span className="job-money-sub">after {formatMoney(j.overhead_share)} overheads</span> : null}
                </span>
              </div>
              <span className={`status-badge ${jobStatusClass(j.status)}`}>{jobStatusLabel(j.status)}</span>
            </button>
          ))}
        </div>
      )}

      <JobModal isOpen={modal} onClose={() => setModal(false)} onSave={(job) => navigate(`/jobs/${job.id}`)} />
    </div>
  );
}
